import { MAX_QUADS } from "./constants.js";
import { mipHeight } from "./heightMips.js";

function aoLevel(sideA, sideB, corner, height) {
    const a = sideA > height ? 1 : 0;
    const b = sideB > height ? 1 : 0;
    if (a && b) return 0;
    const c = corner > height ? 1 : 0;
    return 3 - (a + b + c);
}

export function meshChunk(mips, spec) {
    const { worldSize, chunkSize, cx, cz, scale, minY, skirt } = spec;
    const cells = chunkSize / scale;
    const originI = cx * chunkSize;
    const originJ = cz * chunkSize;

    const positions = new Int16Array(MAX_QUADS * 12);
    const packed = new Uint8Array(MAX_QUADS * 16);
    let quads = 0;
    let minStored = 32767;
    let maxStored = -32768;

    function heightAt(i, j) {
        return mipHeight(mips, worldSize, i, j, scale, minY);
    }

    // Skirts close the crack where this chunk meets a finer one, and only there.
    // edges holds the neighbour's scale on each side, or 0 when it is not finer.
    // A skirt on any other edge hangs inside the terrain with its top edge level
    // with the neighbouring surface, and at distance depth precision lets it
    // show through as a dark line along the chunk border.
    const [edgeEast, edgeWest, edgeNorth, edgeSouth] = spec.edges ?? [0, 0, 0, 0];

    // (i, j) is the first neighbour column along the cell edge. along is 0 when
    // the edge runs along j (east and west) and 1 when it runs along i (north
    // and south). Returns the mesh y the skirt must reach, or null if every
    // finer column is at least as high.
    function skirtBottom(neighbourScale, i, j, along, h) {
        const steps = scale / neighbourScale;
        let lowest = h;
        for (let f = 0; f < steps; f++) {
            const ni = along === 0 ? i : i + f * neighbourScale;
            const nj = along === 0 ? j + f * neighbourScale : j;
            const nh = mipHeight(mips, worldSize, ni, nj, neighbourScale, minY);
            if (nh < lowest) lowest = nh;
        }
        if (lowest >= h) return null;
        return Math.max(lowest, h + 1 - skirt);
    }

    function pushVertex(x, y, z, face, ao, skirtFlag, vp, pp) {
        positions[vp] = x;
        positions[vp + 1] = y;
        positions[vp + 2] = z;
        packed[pp] = face;
        packed[pp + 1] = ao;
        packed[pp + 2] = scale;
        packed[pp + 3] = skirtFlag;
        if (y < minStored) minStored = y;
        if (y > maxStored) maxStored = y;
    }

    function addQuad(corners, face, aos, skirtFlag) {
        if (quads >= MAX_QUADS) return;
        const vp = quads * 12;
        const pp = quads * 16;
        for (let k = 0; k < 4; k++) {
            pushVertex(
                corners[k][0],
                corners[k][1],
                corners[k][2],
                face,
                aos[k],
                skirtFlag,
                vp + k * 3,
                pp + k * 4,
            );
        }
        quads++;
    }

    for (let cj = 0; cj < cells; cj++) {
        const j0 = originJ + cj * scale;
        const z0 = cj * scale;
        const z1 = z0 + scale;
        for (let ci = 0; ci < cells; ci++) {
            const i0 = originI + ci * scale;
            const h = heightAt(i0, j0);
            const top = h + 1;
            const x0 = ci * scale;
            const x1 = x0 + scale;

            const west = heightAt(i0 - scale, j0);
            const east = heightAt(i0 + scale, j0);
            const south = heightAt(i0, j0 - scale);
            const north = heightAt(i0, j0 + scale);
            const sw = heightAt(i0 - scale, j0 - scale);
            const se = heightAt(i0 + scale, j0 - scale);
            const nw = heightAt(i0 - scale, j0 + scale);
            const ne = heightAt(i0 + scale, j0 + scale);

            const aoSW = aoLevel(west, south, sw, h);
            const aoSE = aoLevel(east, south, se, h);
            const aoNE = aoLevel(east, north, ne, h);
            const aoNW = aoLevel(west, north, nw, h);

            addQuad(
                [
                    [x0, top, z1],
                    [x1, top, z1],
                    [x1, top, z0],
                    [x0, top, z0],
                ],
                2,
                [aoNW, aoNE, aoSE, aoSW],
                0,
            );

            if (east < h) {
                const bottom = east + 1;
                addQuad(
                    [
                        [x1, bottom, z0],
                        [x1, top, z0],
                        [x1, top, z1],
                        [x1, bottom, z1],
                    ],
                    0,
                    [3, aoSE, aoNE, 3],
                    0,
                );
            }
            if (west < h) {
                const bottom = west + 1;
                addQuad(
                    [
                        [x0, bottom, z1],
                        [x0, top, z1],
                        [x0, top, z0],
                        [x0, bottom, z0],
                    ],
                    1,
                    [3, aoNW, aoSW, 3],
                    0,
                );
            }
            if (north < h) {
                const bottom = north + 1;
                addQuad(
                    [
                        [x1, bottom, z1],
                        [x1, top, z1],
                        [x0, top, z1],
                        [x0, bottom, z1],
                    ],
                    4,
                    [3, aoNE, aoNW, 3],
                    0,
                );
            }
            if (south < h) {
                const bottom = south + 1;
                addQuad(
                    [
                        [x0, bottom, z0],
                        [x0, top, z0],
                        [x1, top, z0],
                        [x1, bottom, z0],
                    ],
                    5,
                    [3, aoSW, aoSE, 3],
                    0,
                );
            }

            if (ci === cells - 1 && edgeEast > 0) {
                const bottom = skirtBottom(edgeEast, i0 + scale, j0, 0, h);
                if (bottom !== null) {
                    addQuad(
                        [
                            [x1, bottom, z0],
                            [x1, top, z0],
                            [x1, top, z1],
                            [x1, bottom, z1],
                        ],
                        0,
                        [3, 3, 3, 3],
                        1,
                    );
                }
            }
            if (ci === 0 && edgeWest > 0) {
                const bottom = skirtBottom(edgeWest, i0 - edgeWest, j0, 0, h);
                if (bottom !== null) {
                    addQuad(
                        [
                            [x0, bottom, z1],
                            [x0, top, z1],
                            [x0, top, z0],
                            [x0, bottom, z0],
                        ],
                        1,
                        [3, 3, 3, 3],
                        1,
                    );
                }
            }
            if (cj === cells - 1 && edgeNorth > 0) {
                const bottom = skirtBottom(edgeNorth, i0, j0 + scale, 1, h);
                if (bottom !== null) {
                    addQuad(
                        [
                            [x1, bottom, z1],
                            [x1, top, z1],
                            [x0, top, z1],
                            [x0, bottom, z1],
                        ],
                        4,
                        [3, 3, 3, 3],
                        1,
                    );
                }
            }
            if (cj === 0 && edgeSouth > 0) {
                const bottom = skirtBottom(edgeSouth, i0, j0 - edgeSouth, 1, h);
                if (bottom !== null) {
                    addQuad(
                        [
                            [x0, bottom, z0],
                            [x0, top, z0],
                            [x1, top, z0],
                            [x1, bottom, z0],
                        ],
                        5,
                        [3, 3, 3, 3],
                        1,
                    );
                }
            }
        }
    }

    return {
        positions: new Int16Array(positions.subarray(0, quads * 12)),
        packed: new Uint8Array(packed.subarray(0, quads * 16)),
        quadCount: quads,
        minStored: quads > 0 ? minStored : 0,
        maxStored: quads > 0 ? maxStored : 1,
    };
}
